<template>
	<header-block v-if="customer" title="Notifications" bgcolor="white"></header-block>
	<CustomerLoadWait :customer="notifications" />
	<transition>
		<div v-if="notifications" class="notifications">
			<div class="notifications-list">
				<SwipeActionsContainer 
					:items="getDisplayedNotifications()"
					:backgroundColor="'var(--background-color-alt)'"
					:mailStyleSwipe="true"
					:actions="[
						{
							image: require('@/assets/trashcan.png'),
							type: 'delete',
							backgroundColor: 'var(--background-color-alt6)'
						}
					]"
					@action-click="handleAction"
				>
					<template #default="{ item: n }">
						<v-row class="notificationRow" @click="navigateToPage(n)">
							<v-col cols="1" class="iconCol">
								<img v-if="n.pendingMove" :src="require('@/assets/filledbell.png')" class="icon" />
								<img v-else :src="require('@/assets/filledbell.png')" class="icon opace" />
							</v-col>
							<v-col>
								<h2 class="notification-title" :class="{ 'normal-weight': !n.pendingMove }">
									{{ n.title }}
								</h2>
								<div class="notification-body">
									<p :class="{ 'truncated': !expandedNotifications[n.id] }">{{ processMonetaryAmounts(n.body || '') }}</p>
									<a v-if="n.body && n.body.length > 100" @click="(e) => toggleExpand(n.id, e)" class="more-link">
										{{ expandedNotifications[n.id] ? 'Less' : 'More' }}
									</a>
								</div>
								<div class="notification-meta">
									<small>Sent {{ _csharpDateTimetoDateTime(n.sentDateTime) }}</small>
									<img :src="require('@/assets/arrow-right.png')" class="rightIcon" />
								</div>
							</v-col>
						</v-row>
					</template>
				</SwipeActionsContainer>
			</div>

			<div v-if="hasOlderNotifications()" class="older-notifications-toggle">
				<MGButton :outline="true" :noCaps="true" :text="showOlderNotifications ? 'Hide older notifications' : `Show ${getOlderNotificationsCount()} previous notifications`" @click="showOlderNotifications = !showOlderNotifications" />
			</div>

			<div class="stay-in-loop">
				<h2>Stay in the loop</h2>
				<p>Make sure you have notifications enabled for the Money Guided app.</p>
				<MGButton :outline="true"  :arrow="true" :noCaps="true" text="Notification settings" @click="$router.push('notifications')" />
			</div>
		</div>
	</transition>
	
</template>

<script>
import Services from '../mixins/Services';
import CustomerLoadWait from './elements/CustomerLoadWait.vue';
import HeaderBlock from './elements/HeaderBlock.vue';
import Helpers from '../mixins/Helpers';
import SwipeActionsContainer from './elements/SwipeActionsContainer.vue';
import MGButton from './elements/MGButton.vue';

export default {
	mixins: [Services, Helpers],
	components: {CustomerLoadWait, HeaderBlock, SwipeActionsContainer, MGButton},
	data() {
		return {
			notifications: null,
			expandedNotifications: {},
			showOlderNotifications: false,
			pendingMoves: [],
			initialCount: 10
		}
	},
	computed: {
		// Remove all computed properties
	},
	methods: {
		getDisplayedNotifications() {
			if (!this.notifications) return [];
			if (this.showOlderNotifications) return this.notifications;
			return this.notifications.slice(0, this.initialCount);
		},
		hasOlderNotifications() {
			if (!this.notifications) return false;
			return this.notifications.length > this.initialCount;
		},
		getOlderNotificationsCount() {
			if (!this.notifications) return 0;
			return this.notifications.length - this.initialCount;
		},
		onLoad: async function() {
			this._getCustomer();
			this._getNotifications(() => {
				this._getPendingMoves(() => {
					if (this.notifications && this.pendingMoves) {
						this.notifications = this.notifications.map(notification => {
							if (notification.appScreen && notification.appScreen.includes('Moves/')) {
								const moveGuid = notification.appScreen.split('Moves/')[1];
								const matchingMove = this.pendingMoves.find(m => m.move.id === moveGuid);
								if (matchingMove) {
									return {
										...notification,
										pendingMove: matchingMove
									};
								}
								else {
									notification.appScreen = 'noMove';
								}
							}
							return notification;
						});
					}
				}, false);
			});
		},
		handleAction({ item, action } = {}) {
			if (!item || !action) return;
			if (action.type === 'delete') {
				// Create a new array to ensure reactivity
				const updatedNotifications = this.notifications.filter(n => n.id !== item.id);
				this.notifications = updatedNotifications;
				
				// Clean up expanded state
				delete this.expandedNotifications[item.id];
				
				// Mark as deleted in the backend
				this._markAsDeleteNotification(item.id, () => {
					this._clearNotificationCache();
				});
			}
		},
		toggleExpand(id, event) {
			event.stopPropagation();
			this.expandedNotifications[id] = !this.expandedNotifications[id];
		},
		navigateToPage(notification) {
			// Stop event propagation to prevent triggering when clicking the more/less link
			event.stopPropagation();

			this._clearNotificationCache();
			
			// if there is a pendingMove, get its id and navigate to the move page
			if (notification.pendingMove) {
				this.$router.push('/moves/' + notification.pendingMove.id);
			} else {
				// Get the screen, removing "appscreen/Home/" if present, or default to AIOP
				let screen = notification.appScreen || 'Money';
				if (screen === 'noMove') {
					this.$router.push('/CompletedMoves');
				} else {
					this.$router.push('/' + screen);
				}
			}
		},
		processMonetaryAmounts(text) {
			if (localStorage.getItem('randomiseFigures') !== 'true') {
				return text;
			}

			// Match currency symbols (£, $) followed by numbers with optional decimal places
			// Also match numbers followed by currency words (pounds, pence, etc)
			const monetaryPattern = /(?:£|$)(?:\d{1,3}(?:,\d{3})*(?:\.\d{2})?|\d+(?:\.\d{2})?)|(?:\d+(?:\.\d{2})?\s*(?:pounds?|pence|p|GBP|USD))/gi;
			
			return text.replace(monetaryPattern, (match) => {
				// Replace the entire number portion with exactly 4 # characters
				// while preserving currency symbols and words
				if (match.startsWith('£') || match.startsWith('$')) {
					return match.charAt(0) + '####';
				} else {
					// For amounts with currency words, replace the number with ####
					return '####' + match.replace(/^\d+(?:\.\d{2})?/, '');
				}
			});
		}
	},
	mounted: function () {
		this.onLoad();
	},
}
</script>

<style scoped>
.notifications {
	margin-top: -20px;
	margin-left: -30px;
	margin-right: -30px;
}

.notifications-list {
	position: relative;
	z-index: 1;
}

.older-notifications-toggle {
	text-align: center;
	padding: 15px 0;
	background-color: var(--background-color-alt);
}


.notifications {
	margin-top: -20px;
	margin-left: -30px;
	margin-right: -30px;
	span {
		float: right;
	}
	h2 {
		font-size: 16px;
	}
	.meta{
		font-weight: bold;
	}
	.notificationRow{
		margin: 0 !important;
		padding: 15px 30px;
	}
	
	.iconCol {
		padding-right: 30px;
		display: flex;
		align-items: center;
		justify-content: center;
	}
	
	.icon {
		width: 24px;
		border-radius: 50%;
		color: var(--background-color-alt2);
	}
	.opace {
		opacity: 0.5;
	}
	.stay-in-loop{
		padding: 24px 28px 40px;
		text-align: center;
		color: var(--text-color);
		h2 {
			font-size: 16px;
			font-weight: bold;
			margin-bottom: 4px;
		}
		p {
			font-size: 13px;
			margin-bottom: 16px;
		}
	}
}

.middleAlign {
	margin-top:auto;
	margin-bottom:auto;
}

.notification-body {
	position: relative;
}

.notification-body p {
	margin: 0;
	font-size: 14px;
	line-height: 1.4;
	overflow: hidden;
	transition: max-height 0.3s ease-in-out;
	max-height: 1000px; /* Default to expanded */
}

.notification-body p.truncated {
	max-height: 40px;
}

.notification-body p:not(.truncated) {
	max-height: 1000px;
}

.more-link {
	color: var(--text-color);
	text-decoration: underline;
	font-size: 12px;
	cursor: pointer;
	display: inline-block;
	margin-top: 4px;
}

.notification-title {
	margin: 0;
	font-size: 16px;
	font-weight: bold;
}

.notification-meta {
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.normal-weight {
	font-weight: normal;
}

.rightIcon {
	height: 10px;
	margin-right: -10px;
}

</style>
